import type { MonthlyReportExpenseLine } from '../types';

export function newMonthlyExpenseLineId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `exp-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function sumMonthlyExpenseLines(lines: MonthlyReportExpenseLine[] | undefined): number {
  if (!lines?.length) return 0;
  return lines.reduce((sum, line) => sum + (Number(line.amount) || 0), 0);
}

/**
 * Wage / payroll lines in the bank feed are often entered as monthly report expenses; those amounts
 * are also represented as salary (e.g. payroll import gross pay). We exclude them from "operating
 * expenses" so income vs expenses vs salary is not double-counted. Match is on the line label.
 */
export function isSalaryLikeMonthlyExpenseLine(line: MonthlyReportExpenseLine | undefined): boolean {
  if (line?.isWageOrPayrollExpense === true) return true;
  const t = `${line?.label ?? ''}`.toLowerCase();
  if (!t.trim()) return false;
  if (/\b(payroll|remuneration|gross pay|net pay|pay run|salary|myob|xero|payday)\b/.test(t)) return true;
  if (/\b(wages?|wage payment|wage and)\b/.test(t)) return true;
  if (/\b(paye|ird)\b/.test(t) && (/\b(payment|dd|debit|tax)\b/.test(t) || /ird/.test(t))) return true;
  if (/(labou?r|labor)\s*\/\s*wages?/.test(t) || /wages?\s*(&|and)\s*salary/.test(t)) return true;
  if (/\bemployee\b/.test(t) && (/\b(payment|net|gross|pay)\b/.test(t) || /superannuation|kiwisaver/i.test(t)))
    return true;
  return false;
}

export function nonSalaryExpenseLines(
  lines: MonthlyReportExpenseLine[] | undefined
): MonthlyReportExpenseLine[] {
  if (!lines?.length) return [];
  return lines.filter((l) => !isSalaryLikeMonthlyExpenseLine(l));
}

/** Lines that contribute to GST claimable totals in reporting. */
export function gstEligibleExpenseLines(
  lines: MonthlyReportExpenseLine[] | undefined
): MonthlyReportExpenseLine[] {
  if (!lines?.length) return [];
  return lines.filter((l) => !isSalaryLikeMonthlyExpenseLine(l) || l.isWageGstClaimable === true);
}

export function sumMonthlyExpenseLinesExcludingSalaryLike(
  lines: MonthlyReportExpenseLine[] | undefined
): number {
  return sumMonthlyExpenseLines(nonSalaryExpenseLines(lines));
}

/** Portion of expense lines treated as wages/payroll (explicit flag or label match). */
export function sumSalaryLikeExpenseLines(lines: MonthlyReportExpenseLine[] | undefined): number {
  const full = sumMonthlyExpenseLines(lines);
  const operating = sumMonthlyExpenseLinesExcludingSalaryLike(lines);
  return Math.max(0, full - operating);
}

/**
 * Bank/GST line amounts split for wage display: normal wage/payroll (excluded from operating)
 * vs salary/wages where you've marked the line as invoice GST–claimable (`isWageGstClaimable`).
 */
export function sumBankWageSplitForLines(lines: MonthlyReportExpenseLine[] | undefined): {
  wagePayrollExcludedFromOperating: number;
  salaryWageInvoiceInclGst: number;
} {
  let ex = 0;
  let inv = 0;
  for (const line of lines ?? []) {
    if (!isSalaryLikeMonthlyExpenseLine(line)) continue;
    const amt = Math.max(0, Number(line.amount) || 0);
    if (line.isWageGstClaimable === true) inv += amt;
    else ex += amt;
  }
  return { wagePayrollExcludedFromOperating: ex, salaryWageInvoiceInclGst: inv };
}

/** NZ GST included in amount: GST = amount * 3/23 */
export function nzGstFromIncludedAmount(amount: number): number {
  return (Number(amount) || 0) * (3 / 23);
}

export function gstClaimableTotalFromLines(lines: MonthlyReportExpenseLine[] | undefined): number {
  if (!lines?.length) return 0;
  return lines.reduce(
    (sum, line) =>
      sum + (line.gstClaimable === true && !line.zeroGst ? nzGstFromIncludedAmount(line.amount) : 0),
    0
  );
}

/** ANZ import txn id may end with `@@dup@@n` for duplicate bank rows. */
function stripAnzImportDupSuffix(id: string): string {
  return id.replace(/@@dup@@\d+$/i, '');
}

function getImportDateAndAmountFromKey(key: string): { date: string; amount: number } | null {
  const core = stripAnzImportDupSuffix(key);
  const p = core.split('|');
  if (p.length < 2) return null;
  const date = p[0];
  const amount = Number(p[1]);
  if (!/^\d{4}-\d{1,2}-\d{1,2}$/.test(date)) return null;
  if (!Number.isFinite(amount)) return null;
  return { date, amount };
}

/** Any imported bank row key for this calendar day (credits or debits). */
export function hasAnyImportForDate(date: string, importedTxnIds: Record<string, true>): boolean {
  for (const k of Object.keys(importedTxnIds)) {
    const parsed = getImportDateAndAmountFromKey(k);
    if (parsed && parsed.date === date) return true;
  }
  return false;
}

/** At least one imported debit (expense) for this day — needs expense / GST lines. */
export function hasImportDebitForDate(date: string, importedTxnIds: Record<string, true>): boolean {
  for (const k of Object.keys(importedTxnIds)) {
    const parsed = getImportDateAndAmountFromKey(k);
    if (parsed && parsed.date === date && parsed.amount < 0) return true;
  }
  return false;
}

export type ExpenseGstImportContext = { date: string; importedTxnIds: Record<string, true> };

/**
 * True when GST treatment is done for the day:
 * - Lines with amount > 0: each is GST claimable or zero-GST, or
 * - $0 and no lines: only auto-complete if import has rows for this date and **no** debits
 *   (e.g. credits only or empty movement — nothing to allocate). If import has **no** rows for
 *   this date, keep **not** complete (bank file may not include that day yet).
 */
export function expenseLinesGstAllocationComplete(
  lines: MonthlyReportExpenseLine[] | undefined,
  importCtx?: ExpenseGstImportContext
): boolean {
  const substantive = (lines ?? []).filter((l) => (Number(l.amount) || 0) > 0);
  if (substantive.length > 0) {
    return substantive.every((l) => l.gstClaimable === true || l.zeroGst === true);
  }
  const total = sumMonthlyExpenseLines(lines);
  if (total > 0.00001) return false;
  if (!importCtx) return false;
  if (!hasAnyImportForDate(importCtx.date, importCtx.importedTxnIds)) return false;
  if (hasImportDebitForDate(importCtx.date, importCtx.importedTxnIds)) return false;
  return true;
}

export function formatMonthlyExpenseCurrency(value: number): string {
  return `$${Number(value).toFixed(2)}`;
}
